import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IGetSpeakingTestPaper,
  ISpeakingDetail,
  ISpeakingRubricItemScale,
  ISpeakingTestPaper,
} from '../../../models/newSpeaking.model';
import { getCommonCodeLabel } from '../../../lib/store';
import useAxios from '../../../hooks/useAxios';
import { getSpeakingTestPaper } from '../../../api/newSpeaking.api';
import Loader from '../../Loader';
import ErrorResponse from '../../ErrorResponse';
import { Radio, RadioChangeEvent } from 'antd';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import Button from '../../Button';
import useNotification from '../../../hooks/useNotification';
import { BsQuestionCircle } from 'react-icons/bs';
import { TbMoodCry, TbMoodHappy } from 'react-icons/tb';
import { getRank } from '../../../lib/util';
import ImageCarousel from './ImageCarousel';
import GuideModal from './modals/GuideModal';
import _ from 'lodash';
import EvaluationBlock from './EvaluationBlock';

export const IconLevel: FC<{
  color: string;
  label: string;
  icon: ReactNode;
}> = ({ color, label, icon }) => (
  <div className="d-flex flex-row align-items-center">
    {icon}
    <p className="mb-0" style={{ color }}>
      {label}
    </p>
  </div>
);

interface IProps {
  student?: {
    memberEngName: string;
    memberName: string;
    gradeCode: string;
  };
  data: ISpeakingDetail;
  index: number;
  onFinish(answers: string[]): void;
};

const Evaluation: FC<IProps> = ({
  student,
  data,
  index,
  onFinish,
}) => {
  const { t } = useTranslation();
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetSpeakingTestPaper>();
  const { infoNotification } = useNotification();

  const [prevAnswers, setPrevAnswers] = useState<string[]>([]);
  const [answers, setAnswers] = useState<string[]>([]);
  const [prevRes, setPrevRes] = useState<ISpeakingTestPaper[]>([]);
  const [evaluationType, setEvaluationType] = useState('');
  const [visible, setVisible] = useState(false);
  const [scales, setScales] = useState<ISpeakingRubricItemScale[]>([]);

  useEffect(() => {
    if (res) setPrevRes([...prevRes].concat(res?.data));
    setPrevAnswers([...prevAnswers].concat(answers));
    execute(
      getSpeakingTestPaper({
        speakingExamCode: data.speakingExamCode,
        speakingPartCode: data.papers[index]?.speakingPartCode?.trim(),
      }),
    );
  }, [index]);

  useEffect(() => {
    if (res) {
      setAnswers(
        Array.from({ length: res.data.rubric.items.length }, () => ''),
      );
    }
  }, [res]);

  const showModal = (
    newEvaluationType: string,
    newScales: ISpeakingRubricItemScale[],
  ) => {
    setEvaluationType(newEvaluationType);
    setScales(newScales);
    setVisible(true);
  };

  const onCancel = () => {
    setEvaluationType('');
    setScales([]);
    setVisible(false);
  };

  const onAnswerChange = (
    { target: { value } }: RadioChangeEvent,
    index: number,
  ) => {
    const newAnswers = [...answers];
    newAnswers[index] = value;
    setAnswers(newAnswers);
  };

  const getCurrentScore = () => {
    let total = 0;
    if (res) {
      const concatRes = prevRes.concat(res.data);
      prevAnswers.concat(answers).forEach((value) => {
        if (value) {
          const split = value.split('-');
          const evaluationNo = split[0];
          const orderSeq = split[1];
          concatRes.forEach(({ rubric }) => {
            const item = rubric.items.find(
              (item) => item.evaluationNo === +evaluationNo,
            );
            const scoreWeight = item?.scoreWeight;
            const score = item?.scales.find(
              (value) => value.orderSeq === +orderSeq,
            )?.score;
            if (scoreWeight && score) {
              total = total + (score / 100) * scoreWeight;
            }
          });
        }
      });
    }
    return total;
  };

  const getTotalScoreWeight = () => {
    if (res) {
      const concatRes = prevRes.concat(res?.data);
      return concatRes
        .map((item) =>
          item.rubric.items
            .map(({ scoreWeight }) => scoreWeight)
            .reduce((pv, cv) => pv + cv),
        )
        .reduce((pv, cv) => pv + cv);
    } else {
      return 0;
    }
  };

  const getColor = (evaluationType: string) => {
    switch (evaluationType) {
      case 'CO':
        return '#ffc000';
      case 'DI':
        return '#ff0000';
      case 'DE':
        return '#92d050';
      case 'OR':
        return 'cornflowerblue';
      case 'ST':
        return 'rebeccapurple';
    }
  };

  const onNext = (answers: string[]) => {
    for (const answer of answers) {
      if (!answer) return infoNotification(t('cbt.speakTest.message10'));
    }
    onFinish(answers);
  };

  return (
    <>
      <EvaluationBlock>
        {isLoading && <Loader />}
        <ErrorResponse error={error} />
        <div className="flex flex-justify-between">
          <div className="w-400p">
            <table className="table-custom">
              <colgroup>
                <col width="33%" />
                <col width="66%" />
              </colgroup>
              <tbody>
                <tr>
                  <th>
                    {getCommonCodeLabel('TE0008', data.examType, 'ko')}
                    <br />
                    {getCommonCodeLabel('TE0003', data.speakingExamType, 'ko')}
                  </th>
                  <td>{data.papers[index].partName}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="w-400p">
            <b>NAME</b>: {student?.memberEngName || 'English Name'} (
            {student?.memberName || '(Korean)'})
            <br />
            <b>GRADE</b>: {student?.gradeCode || 'Grade Info'}
          </div>
        </div>
        <div className="mx-4 my-4">{res?.data.direction}</div>
        <div className="row mx-1">
          <div className="col-6">
            <ImageCarousel files={res?.data.attachFiles} />
          </div>
          <div className="col-6">
            {res?.data.rubric.items.map(
              ({ name, evaluationType, evaluationNo, scales }, index) => (
                <div className="w-100 mb-3 rubric-content" key={index}>
                  <div
                    className="d-flex flex-row title"
                    style={{ borderColor: getColor(evaluationType) }}
                  >
                    <div
                      className="category px-2"
                      style={{ background: getColor(evaluationType) }}
                    >
                      {name}
                    </div>
                    <div className="title-content">{name}</div>
                  </div>
                  <div className="d-flex align-items-center mt-3 mb-3 justify-content-around">
                    <div
                      className="d-flex flex-column justify-content-center align-items-center mr-3 pointer"
                      onClick={() => showModal(evaluationType, scales)}
                    >
                      <BsQuestionCircle color={getColor(evaluationType)} />
                      <p
                        className="mb-0"
                        style={{ color: getColor(evaluationType) }}
                      >
                        Guide!
                      </p>
                    </div>
                    <IconLevel
                      color={getColor(evaluationType) as string}
                      label="Poor"
                      icon={<TbMoodCry className="mr-3" />}
                    />
                    <div className="w-50 auth-radio__area">
                      <Radio.Group
                        className="d-flex justify-content-between"
                        value={answers[index]}
                        onChange={(e) => onAnswerChange(e, index)}
                      >
                        {_.sortBy(scales, 'orderSeq')
                          .reverse()
                          .map((value, index2) => (
                            <Radio
                              key={`${index}-${index2}`}
                              value={`${evaluationNo}-${value.orderSeq}`}
                            >
                              {value.score}
                            </Radio>
                          ))}
                      </Radio.Group>
                    </div>
                    <IconLevel
                      color={getColor(evaluationType) as string}
                      label="Excellent"
                      icon={<TbMoodHappy className="mr-3" />}
                    />
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
        <div className="row mx-1 d-flex justify-content-end">
          <div className="row footer d-flex align-items-center">
            <div className="footer-label d-flex px-5">
              <p className="mb-0 align-self-center">SCORE</p>
            </div>
            <div className="footer-score d-flex px-5">
              {getCurrentScore()} / {getTotalScoreWeight()}
            </div>
            {index === 1 && (
              <>
                <div className="footer-label d-flex px-5">
                  <p className="mb-0 align-self-center">Rank</p>
                </div>
                <div className="footer-score d-flex px-5">
                  {getRank(getCurrentScore())}
                </div>
              </>
            )}
          </div>
          <div>
            <Button
              className="btn btn-outline-secondary px-5 ml-5"
              onClick={() => onNext(prevAnswers.concat(answers))}
              text={(data?.papers?.length || 0) <= index + 1 ? 'Finish' : 'NEXT'}
            />
          </div>
        </div>
      </EvaluationBlock>
      <GuideModal
        color={getColor(evaluationType)}
        visible={visible}
        data={scales}
        onCancel={onCancel}
      />
    </>
  );
};

export default Evaluation;